/**
 * Ephemeral, session-only store for inbound clipboard clips (NOT in IndexedDB: fail-safe is "clip is gone", not a stale password persisted; spec §1 caps it at 20, session-only).
 * Kept OUT of the notification-list state machine — clips render in a dedicated strip, so the dismiss/reply/mark-read pipeline never acts on a non-notification. Exposes a `useSyncExternalStore` subscribe/getSnapshot pair (stable snapshot between mutations).
 * SECURITY: clip `content` is decrypted notification-equivalent plaintext — never logged, never persisted; held in module-scope memory for the tab's lifetime only.
 */

import type { ClipboardPayload } from "@reflecto/core";

/** Retention/size cap. Locked at 20 by the spec (§1, "in-memory capped 20"). */
export const CLIP_CAP = 20;

/**
 * One stored clip: the decrypted payload plus the local arrival time
 * and a stable client-generated id (the wire carries no clip id, and
 * two clips with identical content+timestamp are still distinct
 * entries). The id keys the React list and the copy-target lookup.
 */
export interface StoredClip {
  /** Stable client-side id (keys the list row; not from the wire). */
  id: string;
  payload: ClipboardPayload;
  /** Epoch ms when this tab received + stored the clip. */
  receivedAt: number;
  /** Wire message id, when known — used for in-session SSE dedup. */
  msgId?: string;
}

// Module-scope state. `snapshot` is the frozen array handed to React;
// it's replaced (never mutated in place) on every change so
// `useSyncExternalStore`'s referential-equality check fires a re-render.
let snapshot: readonly StoredClip[] = [];
const listeners = new Set<() => void>();

// Seen wire `msgId`s, for in-session dedup. The notification-list dedup
// gate in `notifications.ts` checks only the persistent `notifications[]`
// array, which clips never enter — so a live SSE re-delivery of the same
// clip (at-least-once delivery, or a duplicate event before the cursor
// advances) would otherwise add a second strip entry with a fresh UUID.
// `/sync` replay is already cursor-safe; this guards the live-SSE dupe.
// Bounded to the same CLIP_CAP*2 horizon so it can't grow unbounded over
// a long-lived tab.
const seenMsgIds = new Set<string>();
const SEEN_CAP = CLIP_CAP * 2;

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Prepend a clip (most-recent-first) and enforce the size cap, evicting
 * the oldest entries past `CLIP_CAP`. Returns the stored entry so the
 * caller can reference its id (e.g. the push-tap handoff that surfaces
 * the most-recent clip).
 *
 * `msgId` (the wire message id) is optional but, when supplied,
 * deduplicates a re-delivered SSE event: a repeat call with an already
 * seen `msgId` is a no-op (the snapshot is untouched and no listeners
 * fire), regardless of whether the original clip is still in the capped
 * window. It returns the live stored entry when still present, otherwise
 * a non-stored placeholder for the duplicate.
 *
 * `crypto.randomUUID` is available in every browser the PWA targets
 * and in the jsdom/Node test env (the project engine is Node >=24); no
 * fallback needed.
 */
export function addClip(
  payload: ClipboardPayload,
  receivedAt: number = Date.now(),
  msgId?: string
): StoredClip {
  if (msgId !== undefined && seenMsgIds.has(msgId)) {
    // Duplicate delivery — no-op, regardless of snapshot membership. The
    // clip may already have been evicted past CLIP_CAP, in which case
    // `snapshot.find` returns undefined; we must still NOT prepend a
    // second card. Return the live entry when it's still in the capped
    // window; otherwise return a non-stored placeholder describing this
    // duplicate (the snapshot is left untouched and no listeners fire).
    const existing = snapshot.find((c) => c.msgId === msgId);
    return existing ?? { id: crypto.randomUUID(), payload, receivedAt, msgId };
  }
  const clip: StoredClip = { id: crypto.randomUUID(), payload, receivedAt, msgId };
  snapshot = [clip, ...snapshot].slice(0, CLIP_CAP);
  if (msgId !== undefined) {
    seenMsgIds.add(msgId);
    // Trim the oldest seen ids past the bound (Set preserves insertion order).
    while (seenMsgIds.size > SEEN_CAP) {
      const oldest = seenMsgIds.values().next().value;
      if (oldest === undefined) break;
      seenMsgIds.delete(oldest);
    }
  }
  emit();
  return clip;
}

/** Current most-recent-first snapshot. Stable reference between mutations. */
export function getClips(): readonly StoredClip[] {
  return snapshot;
}

/** The most-recently-received clip, or `undefined` when the strip is empty. */
export function getMostRecentClip(): StoredClip | undefined {
  return snapshot[0];
}

/**
 * Drop every clip. Called on disconnect/pairing-lost (wired into
 * `clearLocalState`) so a re-pair starts with an empty strip, and
 * exported for the test reset.
 */
export function clearClips(): void {
  seenMsgIds.clear();
  if (snapshot.length === 0) return;
  snapshot = [];
  emit();
}

/** `useSyncExternalStore` subscribe — returns an unsubscribe fn. */
export function subscribeClips(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
